import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ClearIcon from '@mui/icons-material/Clear';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { S3Client, PutObjectCommand } from '@aws-sdk/client-s3';
import { PDFDocument } from 'pdf-lib';
import { SourceBucket, awsRegion, AnalyzerApiUrl } from '../utilities/constants';
import './UploadSection.css';

const MAX_FILE_SIZE_MB = 50;
const MAX_PAGES = 25;
const LANGUAGE_OPTIONS = ['en-US', 'en-GB', 'fr-FR', 'de-DE', 'es-ES', 'it-IT', 'pt-BR', 'ja-JP', 'zh-CN'];
const DPI_OPTIONS = [72, 96, 150, 200, 300];

function generateSessionId() {
  return `session-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function sanitizeFilename(name) {
  return name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9._-]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
}

// Status pill for individual file in the queue
function FileStatusChip({ status }) {
  if (status === 'queued') return <Chip size="small" icon={<HourglassEmptyIcon fontSize="small" />} label="Queued" sx={{ bgcolor: '#f1f5f9', color: '#475569' }} />;
  if (status === 'uploading') return <Chip size="small" label="Uploading…" color="primary" variant="outlined" />;
  if (status === 'done') return <Chip size="small" icon={<CheckCircleOutlineIcon fontSize="small" />} label="Done" color="success" />;
  if (status === 'error') return <Chip size="small" icon={<ErrorOutlineIcon fontSize="small" />} label="Failed" color="error" />;
  return null;
}

export default function UploadSection({ awsCredentials, userEmail, idToken, onJobCreated, externalPageUsage }) {
  // Shared options (apply to all files in batch)
  const [lang, setLang] = useState('en-US');
  const [dpi, setDpi] = useState(150);
  const [dragging, setDragging] = useState(false);
  const [snack, setSnack] = useState({ open: false, severity: 'error', message: '' });

  // File queue: array of { id, file, pageCount, title, status, error }
  const [fileQueue, setFileQueue] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [overallProgress, setOverallProgress] = useState(0);

  const fileInputRef = useRef();

  const showError = (message) => setSnack({ open: true, severity: 'error', message });
  const showSuccess = (message) => setSnack({ open: true, severity: 'success', message });

  const [pageUsage, setPageUsage] = useState({ limit: 24, consumed: 0, remaining: 24, loading: true });

  useEffect(() => {
    if (externalPageUsage) {
      setPageUsage({ ...externalPageUsage, loading: false });
    }
  }, [externalPageUsage]);

  useEffect(() => {
    if (!idToken) return;
    const fetchUsage = async () => {
      try {
        const resp = await fetch(`${AnalyzerApiUrl}/page-usage`, {
          headers: { Authorization: idToken }
        });
        if (resp.ok) {
          const data = await resp.json();
          setPageUsage({ ...data, loading: false });
        } else {
          setPageUsage(prev => ({ ...prev, loading: false }));
        }
      } catch (err) {
        console.error("Failed to fetch page usage", err);
        setPageUsage(prev => ({ ...prev, loading: false }));
      }
    };
    fetchUsage();
  }, [idToken]);

  // Validate and enqueue a single File object
  const enqueueFile = useCallback(async (f, accumulatedPageCount = 0) => {
    if (!f) return null;
    if (f.type !== 'application/pdf') {
      return { error: `${f.name}: Only PDF files are supported.` };
    }
    if (f.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      return { error: `${f.name}: Exceeds ${MAX_FILE_SIZE_MB} MB limit.` };
    }

    let pages = null;
    try {
      const buffer = await f.arrayBuffer();
      const pdfDoc = await PDFDocument.load(buffer, { ignoreEncryption: true });
      pages = pdfDoc.getPageCount();
    } catch {
      pages = null;
    }

    if (pages !== null && pages > MAX_PAGES) {
      return { error: `${f.name}: Has ${pages} pages — max allowed is ${MAX_PAGES} per document.` };
    }

    if (pages !== null) {
      const projectedConsumed = (pageUsage.consumed || 0) + accumulatedPageCount + pages;
      if (projectedConsumed > (pageUsage.limit || 0)) {
        return { error: `${f.name}: Adding this file would exceed your page limit.` };
      }
    }

    return {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      file: f,
      pageCount: pages,
      title: f.name.replace(/\.pdf$/i, ''),
      status: 'queued',
      error: null,
    };
  }, [pageUsage]);

  // Process a list of File objects, collect errors, enqueue valid ones
  const processFiles = useCallback(async (files) => {
    if (pageUsage.loading) {
      showError('Please wait — still loading account limits…');
      return;
    }

    const pdfFiles = Array.from(files).filter(f => f.name.toLowerCase().endsWith('.pdf') || f.type === 'application/pdf');
    if (pdfFiles.length === 0) {
      showError('No PDF files found in the selection.');
      return;
    }

    const errors = [];
    const newEntries = [];
    let accumulatedPages = fileQueue.reduce((sum, e) => sum + (e.pageCount || 0), 0);

    for (const f of pdfFiles) {
      const result = await enqueueFile(f, accumulatedPages);
      if (result && result.error) {
        errors.push(result.error);
      } else if (result) {
        newEntries.push(result);
        accumulatedPages += result.pageCount || 0;
      }
    }

    if (newEntries.length > 0) {
      setFileQueue(prev => [...prev, ...newEntries]);
    }
    if (errors.length > 0) {
      showError(errors.join('\n'));
    }
  }, [pageUsage, fileQueue, enqueueFile]);

  const handleDrop = async (e) => {
    e.preventDefault();
    setDragging(false);

    // Collect files including from dropped folders
    const allFiles = [];
    const items = e.dataTransfer.items;

    const readEntry = (entry) =>
      new Promise((resolve) => {
        if (entry.isFile) {
          entry.file((f) => { allFiles.push(f); resolve(); });
        } else if (entry.isDirectory) {
          const reader = entry.createReader();
          const readAll = () => {
            reader.readEntries(async (entries) => {
              if (entries.length === 0) { resolve(); return; }
              await Promise.all(entries.map(readEntry));
              readAll();
            });
          };
          readAll();
        } else {
          resolve();
        }
      });

    if (items && items.length > 0 && items[0].webkitGetAsEntry) {
      await Promise.all(Array.from(items).map(item => readEntry(item.webkitGetAsEntry())));
    } else {
      Array.from(e.dataTransfer.files).forEach(f => allFiles.push(f));
    }

    await processFiles(allFiles);
  };

  const handleFileInputChange = async (e) => {
    await processFiles(e.target.files);
    e.target.value = '';
  };

  const removeFile = (id) => {
    setFileQueue(prev => prev.filter(e => e.id !== id));
  };

  const updateTitle = (id, newTitle) => {
    setFileQueue(prev => prev.map(e => e.id === id ? { ...e, title: newTitle } : e));
  };

  const totalPages = fileQueue.reduce((sum, e) => sum + (e.pageCount || 0), 0);
  const canSubmit = fileQueue.length > 0 && fileQueue.some(e => e.status === 'queued') && !isSubmitting;

  const handleSubmitAll = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);

    const queuedEntries = fileQueue.filter(e => e.status === 'queued');
    let completed = 0;

    const s3 = new S3Client({
      region: awsRegion,
      credentials: {
        accessKeyId: awsCredentials.accessKeyId,
        secretAccessKey: awsCredentials.secretAccessKey,
        sessionToken: awsCredentials.sessionToken,
      },
    });

    for (const entry of queuedEntries) {
      // Mark uploading
      setFileQueue(prev => prev.map(e => e.id === entry.id ? { ...e, status: 'uploading' } : e));

      const sessionId = generateSessionId();
      const sanitizedEmail = sanitizeFilename(userEmail || 'anonymous');
      const sanitizedName = sanitizeFilename(entry.file.name);
      const s3Key = `uploads/${sanitizedEmail}/${sessionId}/${sanitizedName}`;

      try {
        const fileBuffer = await entry.file.arrayBuffer();

        await s3.send(
          new PutObjectCommand({
            Bucket: SourceBucket,
            Key: s3Key,
            Body: new Uint8Array(fileBuffer),
            ContentType: 'application/pdf',
            Metadata: {
              'session-id': sessionId,
              'user-email': userEmail || '',
              title: entry.title || entry.file.name,
              lang,
              dpi: String(dpi),
            },
          })
        );

        const resp = await fetch(`${AnalyzerApiUrl}/remediate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: idToken,
          },
          body: JSON.stringify({
            pdf_path: `s3://${SourceBucket}/${s3Key}`,
            session_id: sessionId,
            title: entry.title || entry.file.name,
            lang,
            dpi,
            page_count: entry.pageCount,
          }),
        });

        if (!resp.ok) {
          const err = await resp.json().catch(() => ({}));
          if (resp.status === 429 && err.consumed !== undefined) {
            setPageUsage({ consumed: err.consumed, limit: err.limit, remaining: err.remaining, loading: false });
          }
          throw new Error(err.error || `Failed to start remediation (${resp.status})`);
        }

        const data = await resp.json();
        const requestId = data.request_id;

        const newJob = {
          requestId,
          sessionId,
          fileName: entry.file.name,
          s3Key,
          title: entry.title || entry.file.name,
          lang,
          dpi,
          pageCount: entry.pageCount,
          status: 'processing',
          submittedAt: new Date().toISOString(),
          pdfPath: `s3://${SourceBucket}/${s3Key}`,
        };

        if (entry.pageCount) {
          setPageUsage(prev => ({
            ...prev,
            consumed: prev.consumed + entry.pageCount,
            remaining: Math.max(0, prev.remaining - entry.pageCount),
          }));
        }

        onJobCreated(newJob);
        setFileQueue(prev => prev.map(e => e.id === entry.id ? { ...e, status: 'done' } : e));
      } catch (err) {
        console.error(err);
        setFileQueue(prev => prev.map(e => e.id === entry.id ? { ...e, status: 'error', error: err.message } : e));
      }

      completed += 1;
      setOverallProgress(Math.round((completed / queuedEntries.length) * 100));
    }

    const doneCount = fileQueue.filter(e => e.status === 'done').length + queuedEntries.filter((_, i) => {
      // approximate — actual state is set async
      return true;
    }).length;

    showSuccess(`Submitted ${queuedEntries.length} file${queuedEntries.length !== 1 ? 's' : ''} — jobs started!`);
    setIsSubmitting(false);
    setOverallProgress(0);

    // Clear only successfully processed entries after a short delay
    setTimeout(() => {
      setFileQueue(prev => prev.filter(e => e.status !== 'done'));
    }, 2000);
  };

  const clearAll = () => {
    if (!isSubmitting) setFileQueue([]);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="upload-root"
    >
      <Typography component="h1" variant="h5" sx={{ fontWeight: 700, mb: 0.5, color: '#0f0e2e' }}>
        Upload PDFs
      </Typography>
      <Typography variant="body2" sx={{ color: '#475569', mb: 3 }}>
        Drop or select one or more PDFs. Each document will be queued and processed separately.
      </Typography>

      {/* Drop zone */}
      <div
        className={`drop-zone ${dragging ? 'dragging' : ''} ${fileQueue.length > 0 ? 'has-files' : ''}`}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => fileQueue.length === 0 && fileInputRef.current?.click()}
        role={fileQueue.length === 0 ? 'button' : undefined}
        tabIndex={fileQueue.length === 0 ? 0 : undefined}
        aria-label="Drop zone — click or press Enter to select PDFs"
        onKeyDown={(e) => {
          if (fileQueue.length === 0 && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            fileInputRef.current?.click();
          }
        }}
      >
        {/* Hidden file inputs */}
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf,.pdf"
          multiple
          hidden
          onChange={handleFileInputChange}
          id="bulk-file-input"
        />

        {fileQueue.length === 0 ? (
          <div className="drop-zone-empty">
            <CloudUploadIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
            <Typography variant="body1" sx={{ fontWeight: 600, color: '#475569' }}>
              Drag &amp; drop PDFs here
            </Typography>
            <Typography variant="body2" sx={{ color: '#94a3b8', mb: 2 }}>
              or use the button below
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<PictureAsPdfIcon />}
                onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                sx={{ borderColor: '#5F5BCD', color: '#5F5BCD', '&:hover': { borderColor: '#4e4ab5', bgcolor: 'rgba(95,91,205,0.05)' } }}
              >
                Select Files
              </Button>
            </Box>
          </div>
        ) : (
          <div className="drop-zone-files-summary" onClick={(e) => e.stopPropagation()}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f0e2e' }}>
                {fileQueue.length} file{fileQueue.length !== 1 ? 's' : ''} queued
                {totalPages > 0 && ` · ${totalPages} total pages`}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isSubmitting}
                  sx={{ color: '#5F5BCD', fontSize: '0.75rem' }}
                >
                  Add Files
                </Button>
                <Button
                  size="small"
                  onClick={clearAll}
                  disabled={isSubmitting}
                  sx={{ color: '#94a3b8', fontSize: '0.75rem' }}
                >
                  Clear All
                </Button>
              </Box>
            </Box>

            {/* File list */}
            <Box sx={{ maxHeight: 280, overflowY: 'auto', pr: 0.5 }}>
              <AnimatePresence initial={false}>
                {fileQueue.map((entry) => (
                  <motion.div
                    key={entry.id}
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Paper
                      variant="outlined"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        p: 1,
                        mb: 0.75,
                        borderRadius: 1.5,
                        borderColor: entry.status === 'error' ? '#fca5a5' : entry.status === 'done' ? '#86efac' : '#e2e8f0',
                        bgcolor: entry.status === 'error' ? '#fff5f5' : entry.status === 'done' ? '#f0fdf4' : '#fff',
                      }}
                    >
                      <PictureAsPdfIcon sx={{ fontSize: 22, color: '#5F5BCD', flexShrink: 0 }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography noWrap variant="body2" sx={{ fontWeight: 600, color: '#0f0e2e', fontSize: '0.8rem' }}>
                          {entry.file.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          {(entry.file.size / 1024 / 1024).toFixed(2)} MB
                          {entry.pageCount !== null ? ` · ${entry.pageCount} page${entry.pageCount !== 1 ? 's' : ''}` : ''}
                        </Typography>
                        {entry.status === 'error' && (
                          <Typography variant="caption" sx={{ display: 'block', color: '#dc2626', mt: 0.25 }}>
                            {entry.error}
                          </Typography>
                        )}
                      </Box>
                      <FileStatusChip status={entry.status} />
                      {entry.status === 'queued' && !isSubmitting && (
                        <IconButton size="small" onClick={() => removeFile(entry.id)} sx={{ flexShrink: 0 }}>
                          <ClearIcon fontSize="small" sx={{ color: '#94a3b8' }} />
                        </IconButton>
                      )}
                    </Paper>
                  </motion.div>
                ))}
              </AnimatePresence>
            </Box>
          </div>
        )}
      </div>

      {/* Shared Job Options */}
      {fileQueue.length > 0 && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          transition={{ duration: 0.3 }}
        >
          <Paper variant="outlined" sx={{ p: 2.5, mt: 2, borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f0e2e', mb: 2 }}>
              Batch Options <Typography component="span" variant="caption" sx={{ color: '#94a3b8', fontWeight: 400 }}>(applied to all files)</Typography>
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                select
                label="Language"
                value={lang}
                onChange={(e) => setLang(e.target.value)}
                size="small"
                sx={{ minWidth: 160 }}
                disabled={isSubmitting}
              >
                {LANGUAGE_OPTIONS.map((l) => (
                  <MenuItem key={l} value={l}>{l}</MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Render DPI"
                value={dpi}
                onChange={(e) => setDpi(Number(e.target.value))}
                size="small"
                sx={{ minWidth: 140 }}
                helperText="Higher = better quality, slower"
                disabled={isSubmitting}
              >
                {DPI_OPTIONS.map((d) => (
                  <MenuItem key={d} value={d}>{d}</MenuItem>
                ))}
              </TextField>
            </Box>
          </Paper>
        </motion.div>
      )}

      <Divider sx={{ my: 3 }} />

      {/* Submit button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
        {fileQueue.length > 0 && (
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            {fileQueue.filter(e => e.status === 'queued').length} ready to submit
          </Typography>
        )}
        <Button
          variant="contained"
          size="large"
          disabled={!canSubmit}
          onClick={handleSubmitAll}
          startIcon={<CloudUploadIcon />}
          sx={{
            backgroundColor: '#5F5BCD',
            px: 4,
            '&:hover': { backgroundColor: '#4e4ab5' },
          }}
        >
          {isSubmitting
            ? `Uploading ${fileQueue.filter(e => e.status === 'uploading').length > 0
              ? fileQueue.find(e => e.status === 'uploading')?.file?.name?.slice(0, 20) + '…'
              : '…'}`
            : `Start Remediation${fileQueue.length > 1 ? ` (${fileQueue.filter(e => e.status === 'queued').length} files)` : ''}`}
        </Button>
      </Box>

      {isSubmitting && (
        <Box sx={{ mt: 2 }}>
          <LinearProgress
            variant="determinate"
            value={overallProgress}
            sx={{
              borderRadius: 1,
              '& .MuiLinearProgress-bar': { backgroundColor: '#5F5BCD' },
            }}
          />
          <Typography variant="caption" sx={{ color: '#64748b', mt: 0.5, display: 'block', textAlign: 'right' }}>
            {overallProgress}% complete
          </Typography>
        </Box>
      )}

      <Snackbar
        open={snack.open}
        autoHideDuration={6000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ whiteSpace: 'pre-line' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </motion.div>
  );
}
